import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Flag,
  Calendar,
  MapPin,
  Globe,
  Info,
  Megaphone,
  Plus,
  FlaskConical,
} from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { ElectionsApi } from '@/services/political/electionsApi'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { CampaignTable } from '../../components/CampaignTable'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { formatDate } from '../../utils/format'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null
  return (
    <div className="p-field full" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ad-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      <span style={{ fontSize: 14, color: 'var(--ad-text)' }}>{value}</span>
    </div>
  )
}

export function ElectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()

  const { data: election, isLoading: electionLoading } = useQuery({
    queryKey: ['election', id],
    queryFn: () => ElectionsApi.getById(id!),
    enabled: !!id && hasToken,
  })

  const { data: allCampaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['politicalCampaigns'],
    queryFn: () => PoliticalCampaignsApi.getAll(),
    enabled: hasToken,
  })

  const [campaignSearch, setCampaignSearch] = useState('')
  const linkedCampaigns = filterBySearch(allCampaigns.filter((c) => c.electionId === id), campaignSearch)

  const crumbs = [
    { label: t('elections.title'), href: '/political/elections' },
    { label: election?.name ?? '…' },
  ]

  if (electionLoading) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  if (!election) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <div className="empty-state">
          <Flag className="size-6" />
          <div className="empty-state__title">{t('elections.notFound')}</div>
        </div>
      </AppShell>
    )
  }

  const formattedDate = election.electionDate
    ? formatDate(election.electionDate, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return (
    <AppShell crumbs={crumbs} showBack>
      <div className="ad-page">
        <ValidationWarnings warnings={election.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{election.name}</h1>
            {election.description && <p>{election.description}</p>}
          </div>
          <div className="ad-page__actions">
            <VersionHistoryButton />
          </div>
        </div>

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            {/* Details card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Info} />
                  {t('common.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <DetailRow
                    label={t('elections.electionDate')}
                    value={
                      formattedDate ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Calendar className="size-3.5" style={{ color: 'var(--ad-text-muted)' }} />
                          {formattedDate}
                        </span>
                      ) : null
                    }
                  />
                  <DetailRow
                    label={t('elections.type')}
                    value={election.type || null}
                  />
                  <DetailRow
                    label={t('elections.location')}
                    value={
                      election.location ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MapPin className="size-3.5" style={{ color: 'var(--ad-text-muted)' }} />
                          {election.location}
                        </span>
                      ) : null
                    }
                  />
                  <DetailRow
                    label={t('elections.infoUrl')}
                    value={
                      election.infoUrl ? (
                        <a
                          href={election.infoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ad-primary)', textDecoration: 'none' }}
                        >
                          <Globe className="size-3.5" />
                          {election.infoUrl}
                        </a>
                      ) : null
                    }
                  />
                  {election.testOnly && (
                    <DetailRow
                      label={t('elections.testOnly')}
                      value={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FlaskConical className="size-3.5" style={{ color: 'var(--ad-text-muted)' }} />
                          {t('elections.testOnlyLabel')}
                        </span>
                      }
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Campaigns panel */}
          <div className="ad-panel">
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center gap-3">
                <CardTitle>
                  <CardIcon icon={Megaphone} />
                  {t('political.campaigns')}
                </CardTitle>
                <SearchInput value={campaignSearch} onChange={setCampaignSearch} className="ml-auto" />
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/political/campaigns/${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}?new=true&election=${id}`)
                  }
                >
                  <Plus className="size-4" />
                  {t('political.newCampaign')}
                </Button>
              </CardHeader>
              <CardContent style={{ padding: 0 }}>
                <CampaignTable
                  campaigns={linkedCampaigns}
                  loading={campaignsLoading}
                  emptyMessage={t('elections.noCampaigns')}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
